import { Chukker, Chukkers } from "../../shared/types/chukkers.type";
import {
    Box,
    Button,
    Card, CardActions,
    CardContent, IconButton, InputProps,
    MenuItem,
    Select, Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow, TextField, Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { AccessTime, Delete } from "@mui/icons-material";
import { Icon } from '@iconify/react'
import { debounce, difference, isEmpty, isEqual } from "lodash";
import ReactInputMask from "react-input-mask";
import { useDispatch, useSelector } from "react-redux";
import { toggleDispatchCancelChukker, toggleDispatchSaveChukker, toggleIsChukkerEditedBannerVisible } from "../../store/reducers/display.reducer";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";
import { startTimeFrom0 } from "../../shared/utils/MapSection";

type ChukkersTableProps = {
    chukkers: Chukkers,
    onSaveChukkers: (chukkers: Row[]) => void,
    time: number[],
    currentIndex: number,
    trainingId?: string,
}

type Row =
    Pick<Chukker, 'manual_index_start' | 'manual_index_end' | 'order_index'>
    & {
        is_supplementary?: boolean | never,
        training?: string,
    }

const mapChukkersToRows = (chukkers: Chukker[]): Row[] =>
    chukkers.map((c, index) => ({
        manual_index_start: c?.manual_index_start || c.index_start,
        manual_index_end: c.manual_index_end || c.index_end,
        ...(c.order_index === 9 ? { is_supplementary: true } : {}),
        order_index: index + 1,
    }));


export const ChukkersTable = ({ trainingId, onSaveChukkers, chukkers, time, currentIndex }: ChukkersTableProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [rows, setRows] = useState<Row[]>([]);
    const [chukkersNumber, setChukkersNumber] = useState<number[]>([1, 2, 3, 4, 5, 6, 7, 8, 9]);

    const {
        isChukkerEditedBannerVisible,
        dispatchSaveChukker,
        dispatchCancelChukker
    } = useSelector<RootState>(state => state.display) as any

    const initialChukkers = useMemo(() => mapChukkersToRows(chukkers), [chukkers]);

    const formattedTime = useMemo(() => {
        return startTimeFrom0(time)
    }, [time])

    useEffect(() => {
        if (isEmpty(chukkers)) return

        setRows(mapChukkersToRows(chukkers));
    }, [chukkers]);

    useEffect(() => {
        handleBannerDisplay()
        return () => handleBannerDisplay.cancel()
    }, [initialChukkers, rows]);


    useEffect(() => {
        if (dispatchSaveChukker) {
            saveExams()
            dispatch(toggleDispatchSaveChukker(false))
        }
    }, [dispatchSaveChukker, rows])

    useEffect(() => {
        if (dispatchCancelChukker) {
            setRows(mapChukkersToRows(chukkers));
            dispatch(toggleDispatchCancelChukker(false))
        }
    }, [dispatchCancelChukker]);

    const handleBannerDisplay = debounce(() => {

        const isDirty = !isEqual(rows, initialChukkers);

        dispatch(toggleIsChukkerEditedBannerVisible(isDirty))

    }, 200)


    //Get Time duration to mm:ss
    const getSecToTime = (index: number) => {
        const value = formattedTime[index];

        return dayjs.duration(value, 'seconds').format('mm:ss');
    }

    const updateIndex = (
        idx: number,
        field: keyof Row,
        value: number
    ) => {
        setRows(prev =>
            prev.map((row, index) =>
                index === idx ? { ...row, [field]: value } : row
            )
        );
    };

    const addNewChukker = () => {

        const rowsLength = rows.length

        const newRow: Row = {
            manual_index_start: currentIndex,
            manual_index_end: currentIndex + 2,
            ...(rowsLength === 9 ? { is_supplementary: true } : {}),
            order_index: rowsLength + 1
        }

        setRows((prev) => [...prev, newRow])
    }


    const updateOrderIndex = (index: number, value: number) => {

        updateIndex(index, 'order_index', value)

    }

    const isNumberDisabled = (value: number): boolean => {

        return rows.map(row => row.order_index).includes(value);
    }

    const removeChukker = (index: number) => {
        setRows(prev => [...prev].filter((item, idx) => index !== idx))
    }

    const saveExams = () => {
        onSaveChukkers(rows.map(row => ({ ...row, training: trainingId })))
    }


    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant={'subtitle2'}>{t('chukkers:tableTitle')}</Typography>
                    <Button disabled={rows.length === 9} onClick={addNewChukker} startIcon={<AddIcon />}
                        sx={{ float: 'right', mb: 2 }}
                        variant={'outlined'}>{t('chukkers:addNew')}</Button>
                </Box>

                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('chukkers:table:number')}</TableCell>
                            <TableCell>{t('chukkers:table:start')}</TableCell>
                            <TableCell>{t('chukkers:table:end')}</TableCell>
                            <TableCell sx={{ width: '20%' }}>{t('equimetre:table:actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            isEmpty(rows) ?
                                <TableRow>
                                    <TableCell sx={{ textAlign: 'center', pt: 3 }} colSpan={4}>
                                        {t('chukkers:empty')}
                                    </TableCell>
                                </TableRow>
                                :
                                rows.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Select
                                                value={row.order_index}
                                                fullWidth
                                                size="small"
                                                onChange={(e) => {
                                                    const value = e.target.value as number;
                                                    updateOrderIndex(index, value)
                                                }}
                                            >
                                                {chukkersNumber.map((number, i) => (
                                                    <MenuItem key={number} disabled={isNumberDisabled(number)}
                                                        selected={Boolean(row?.order_index)}
                                                        value={number}>{number !== 9 ? number : t('chukkers:supp')}</MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <ReactInputMask
                                                    mask="99:99" maskChar="0"
                                                    value={getSecToTime(row.manual_index_start)}
                                                >

                                                    {(inputProps) => <TextField fullWidth
                                                        inputProps={{ readOnly: true }}
                                                        size="small" />}

                                                </ReactInputMask>
                                                <IconButton onClick={() => {
                                                    updateIndex(index, 'manual_index_start', currentIndex)
                                                }}>
                                                    <Icon icon={'mdi:timer-edit-outline'} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <ReactInputMask
                                                    mask="99:99" maskChar="_"
                                                    value={getSecToTime(row.manual_index_end)}
                                                >
                                                    {(inputProps) => <TextField fullWidth
                                                        inputProps={{ readOnly: true }}
                                                        size="small" />}

                                                </ReactInputMask>
                                                <IconButton onClick={() => {
                                                    updateIndex(index, 'manual_index_end', currentIndex)
                                                }}>
                                                    <Icon icon={'mdi:timer-edit-outline'} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton color="error" onClick={() => {
                                                removeChukker(index)
                                            }}>
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                        }

                    </TableBody>
                </Table>
                <Button disabled={!isChukkerEditedBannerVisible} onClick={saveExams} sx={{ float: 'right', mb: 2, mt: 4 }}
                    variant={'contained'}>{t('button:save')}</Button>
            </CardContent>

        </Card>
    )
}